<script>
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import MaskedInput from "vue-text-mask";
import DatePicker from "vue2-datepicker";

export default {
    name: "StockTransactionModal",
    components: {
        MaskedInput,
        DatePicker
    },
    props: {
        modalTitle: {
            type: String,
            default: "Stock Transaction",
        },
        transactionType: {
            type: Number,
            default: 0, // 0 = credit, 1 = debit
        },
        maxCredit: {
            type: Number,
            default: 0,
        },
        deliveryUnits: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            form: {
                stockItemId: null,
                description: "",
                debit: null,
                credit: null,
                unitCost: "",
                referenceNumber: "",
                iarItemId: null,
                deliveryUnitId: null,
                deliveryUnit: "",
                stockItemTransactionParentId: null,
                maxCredit: null,
                dateTime: Date.now()
            },
            submitted: false,
            isSubmitting: false,
            currencyMask: createNumberMask({
                prefix: "",
                allowDecimal: true
            }),
            quantityMask: createNumberMask({
                prefix: "",
                includeThousandsSeparator: false
            }),
            loadingRIS: false,
            autoGenerate: true,
        };
    },
    computed: {
        isEditMode() {
            return this.form.id > 0;
        },
        isDebit() {
            return this.transactionType === 1;
        },
        isCredit() {
            return this.transactionType === 0;
        },
    },
    watch: {
        autoGenerate(newVal) {
            if (newVal) {
                this.form.referenceNumber = "";
                this.generateRISNumber();
            } else {
                this.form.referenceNumber = "";
            }
        }
    },
    methods: {
        resetForm(parentId = 0) {
            this.form = {
                stockItemId: null,
                description: "",
                debit: null,
                credit: null,
                unitCost: "",
                referenceNumber: "",
                iarItemId: null,
                deliveryUnitId: 0,
                deliveryUnit: "",
                stockItemTransactionParentId: parentId,
                maxCredit: null,
                dateTime: Date.now()
            };
            this.submitted = false;
            this.isSubmitting = false;
        },
        async generateRISNumber() {
            this.loadingRIS = true;
            try{
                const {data: response} = await this.$store.dispatch("requisition/GenerateRequisitionNumber", this.$store.state.data.selectedCampus.id);
                if(response.error){
                    this.form.referenceNumber = "";
                    return;
                }
                this.form.referenceNumber = response;
            }catch(error){
                this.form.referenceNumber = "";
            }
            this.loadingRIS = false;
        },
        updateData(data) {
            this.form = JSON.parse(JSON.stringify(data));
            this.form.unitCost = data.unitCost ? data.unitCost.toString() : "";
            this.form.debit = data.debit ? data.debit.toString() : "";
            this.form.credit = data.credit ? data.credit.toString() : "";
            this.submitted = false;
            this.isSubmitting = false;
        },
        async submitForm() {
            this.submitted = true;
            
            if (this.checkTransForm()) {
                return;
            }
            this.isSubmitting = true;
            let data = JSON.parse(JSON.stringify(this.form));
            data.dateTime = this.getDate(new Date(this.form.dateTime));
            if (this.isCredit) {
                data.debit = 0;
            }
            if (this.isDebit) {
                data.credit = 0;
                data.deliveryUnitId = 0;
            }
            this.$emit("submit", data);
        },
        checkTransForm() {
            if (this.form.dateTime === null || this.form.dateTime === "") {
                return true;
            }

            if (this.isCredit) {
                if (
                    this.form.credit === null ||
                    this.form.credit === "" ||
                    this.form.referenceNumber === "" ||
                    this.form.deliveryUnitId === null
                ) {
                    return true;
                }
                return false;
            } else if (this.isDebit) {
                if (
                    this.form.debit === null ||
                    this.form.debit === "" ||
                    this.form.referenceNumber === "" ||
                    this.form.unitCost === null
                ) {
                    return true;
                }
                return false;
            }
        },
        checkCredit() {
            if (parseFloat(this.form.credit) > parseFloat(this.maxCredit)) {
                this.form.credit = this.maxCredit.toString();
            }
        },
    },
};
</script>

<template>
    <b-modal
        id="stock-transaction-modal"
        :title="modalTitle"
        size="md"
        title-class="font-18"
        hide-footer
        no-close-on-backdrop
    >
        <form @submit.prevent="submitForm" class="needs-validation">
            <div class="mb-3">
                <label for="dateTime">
                    Date &amp; Time 
                </label>
                <date-picker
                    v-model="form.dateTime"
                    :first-day-of-week="1"
                    lang="en"
                    type="datetime"
                    value-type="timestamp"
                    format="YYYY-MM-DD hh:mm a"
                    :class="{
                        'is-invalid': submitted && (form.dateTime === '' || form.dateTime === null),
                    }"
                ></date-picker>
                <div
                    v-if="submitted && (form.dateTime === '' || form.dateTime === null)"
                    class="invalid-feedback"
                >
                    This value is required.
                </div>
            </div>

            <div v-if="isDebit">
                <!-- Debit Transaction -->
                <div class="mb-3">
                    <label for="referenceNumber" >
                        Reference Number (P.O No)
                    </label>
                    <b-form-input
                        id="referenceNumber"
                        v-model="form.referenceNumber"
                        type="text"
                        placeholder="Enter reference number"
                        autocomplete="off"
                        :class="{
                            'is-invalid': submitted && (form.referenceNumber === '' || form.referenceNumber === null),
                        }"
                    ></b-form-input>
                    <div
                        v-if="submitted && (form.referenceNumber === '' || form.referenceNumber === null)"
                        class="invalid-feedback"
                    >
                        This value is required.
                    </div>
                </div>

                <div class="mb-3">
                    <label for="debit" >
                        Quantity (Debit) 
                    </label>
                    <MaskedInput
                        id="debit"
                        v-model="form.debit"
                        :mask="quantityMask"
                        type="text"
                        class="form-control"
                        placeholder="Enter quantity"
                        autocomplete="off"
                        :class="{
                            'is-invalid': submitted && (form.debit === '' || form.debit === null),
                        }"
                    />
                    <div
                        v-if="submitted && (form.debit === '' || form.debit === null)"
                        class="invalid-feedback"
                    >
                        This value is required.
                    </div>
                </div>

                <div class="mb-3">
                    <label for="unitCost" >
                        Unit Cost 
                    </label>
                    <MaskedInput
                        id="unitCost"
                        v-model="form.unitCost"
                        :mask="currencyMask"
                        type="text"
                        class="form-control"
                        placeholder="Enter unit cost"
                        autocomplete="off"
                        :class="{
                            'is-invalid': submitted && (form.unitCost === '' || form.unitCost === null),
                        }"
                    />
                    <div
                        v-if="submitted && (form.unitCost === '' || form.unitCost === null)"
                        class="invalid-feedback"
                    >
                        This value is required.
                    </div>
                </div>
            </div>

            <div v-if="isCredit">
                <!-- Credit Transaction -->
                <div class="mb-3">
                    <label for="referenceNumber" class="d-flex justify-content-between">
                        RIS Number 
                        <div class="form-check form-checkbox-outline form-check-success">
                            <input
                                type="checkbox"
                                id="autoGenerate"
                                class="form-check-input"
                                v-model="autoGenerate"
                            /><label for="autoGenerate" class="form-check-label">
                                Auto Generate Number
                            </label>
                        </div>
                    </label>
                    <b-input-group>
                        <template #append>
                            <b-button @click="generateRISNumber" variant="outline-info" v-if="!loadingRIS && !form.referenceNumber && autoGenerate">
                                <i class="mdi mdi-reload"></i>
                            </b-button>
                        </template>
                        <div class="form-control bg-light text-muted" v-if="loadingRIS">
                            <i
                                class="spinner-border spinner-border-sm me-2"
                                role="status"
                            ></i>
                        </div>
                        <template v-else>
                            <input
                                v-if="autoGenerate"
                                id="referenceNumber"
                                v-model="form.referenceNumber"
                                type="text"
                                placeholder="Enter RIS number"
                                autocomplete="off"
                                class="form-control"
                                disabled
                                :class="{
                                    'is-invalid': submitted && (form.referenceNumber === '' || form.referenceNumber === null),
                                }"
                            />
                            <input
                                v-else
                                id="referenceNumber"
                                v-model="form.referenceNumber"
                                type="text"
                                placeholder="Enter RIS number"
                                autocomplete="off"
                                class="form-control"
                                :class="{
                                    'is-invalid': submitted && (form.referenceNumber === '' || form.referenceNumber === null),
                                }"
                            />
                        </template>
                    </b-input-group>
                    <div
                        v-if="submitted && (form.referenceNumber === '' || form.referenceNumber === null)"
                        class="invalid-feedback"
                    >
                        This value is required.
                    </div>
                </div>

                <div class="mb-3">
                    <label for="credit" >
                        Quantity (Credit) 
                        <small class="text-muted">(Max: {{ maxCredit }})</small>
                    </label>
                    <MaskedInput
                        id="credit"
                        v-model="form.credit"
                        :mask="quantityMask"
                        type="text"
                        class="form-control"
                        placeholder="Enter quantity"
                        autocomplete="off"
                        @input="checkCredit"
                        :class="{
                            'is-invalid': submitted && (form.credit === '' || form.credit === null),
                        }"
                    />
                    <div
                        v-if="submitted && (form.credit === '' || form.credit === null)"
                        class="invalid-feedback"
                    >
                        This value is required.
                    </div>
                </div>

                <div class="mb-3">
                    <label for="deliveryUnitId" >
                        Delivery Unit 
                    </label>
                    <b-form-select
                        id="deliveryUnitId"
                        class="form-select"
                        v-model="form.deliveryUnitId"
                        autocomplete="off"
                        :class="{
                            'is-invalid': submitted && form.deliveryUnitId === null,
                        }"
                    >
                        <template #first>
                            <b-form-select-option :value="null" disabled>
                                -- Select Delivery Unit --
                            </b-form-select-option>
                        </template>
                        <b-form-select-option v-for="unit in deliveryUnits" :key="unit.id" :value="unit.id">
                            {{ unit.name }}
                        </b-form-select-option>
                    </b-form-select>
                    <div
                        v-if="submitted && form.deliveryUnitId === null"
                        class="invalid-feedback"
                    >
                        This value is required.
                    </div>
                </div>
            </div>

            <div class="mb-3">
                <label for="description" >
                    Description 
                </label>
                <b-form-textarea
                    id="description"
                    v-model="form.description"
                    placeholder="Enter description"
                    rows="3"
                    autocomplete="off"
                ></b-form-textarea>
            </div>

            <!-- Modal Footer -->
            <div class="d-flex justify-content-end gap-2 pt-3 border-top">
                <b-button
                    type="submit"
                    variant="primary"
                    :disabled="isSubmitting"
                >
                    <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                    <i class="bx bx-save"></i> {{ isEditMode ? 'Update' : 'Create' }} Transaction
                </b-button>
                <b-button
                    variant="text"
                    @click="$bvModal.hide('stock-transaction-modal')"
                    :disabled="isSubmitting"
                >
                    Cancel
                </b-button>
            </div>
        </form>
    </b-modal>
</template>

<style scoped>
.form-label {
    font-weight: 600;
    color: #495057;
}

.text-danger {
    color: #dc3545 !important;
}

.spinner-border-sm {
    width: 1rem;
    height: 1rem;
}

.border-top {
    border-top: 1px solid #dee2e6 !important;
}
</style>